import React, { useContext, useState } from 'react';
import { useTheme } from 'styled-components';
import { Registration } from '@stax-core/types-omni';
import { RegistrationStore, updateRegistration } from '../../context';
import { Button, Group } from '../';
import { coreapi, onboardingapi } from '../../api';
import { useAuthToken, useToaster } from '../../hooks';
import { removeLinkedPlaidAccount } from '../../util/plaid.util';
import { PlaidAccountBase } from '../../pages/financial/FinancialFields.types';
import { AccountUnlinkModal } from './AccountUnlinkModal';
import BankGenericIcon from '../../assets/img/institution/generic-bank.svg';

export const PlaidAccountCards = (props: {
  accounts: PlaidAccountBase[];
  updatingAccounts: boolean;
  updateAccountNumbers: () => Promise<void>;
  setUpdatingAccounts: React.Dispatch<React.SetStateAction<boolean>>;
  setLinkToken: React.Dispatch<React.SetStateAction<string | null>>;
}) => {
  const {
    accounts,
    updatingAccounts,
    updateAccountNumbers,
    setUpdatingAccounts,
    setLinkToken,
  } = props;
  const { authToken } = useAuthToken();
  const {
    dispatch,
    state: { registration },
  } = useContext(RegistrationStore);
  const theme = useTheme();
  const { toaster, toast } = useToaster();
  const [showPrimaryRow, setShowPrimaryRow] = useState('inherit');
  const [showSecondaryRow, setShowSecondaryRow] = useState('inherit');
  const [showPrimaryModal, setShowPrimaryModal] = useState(false);
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);

  const accountList = accounts.map((account, key) => {
    const plaidId = account.plaid_token_id;
    const setShowAccountRow =
      account.account_type === 'primary'
        ? setShowPrimaryRow
        : setShowSecondaryRow;

    const removeAccountPlaid = async () => {
      try {
        setShowAccountRow('none');
        setUpdatingAccounts(true);
        const { registrationChanges } = await removeLinkedPlaidAccount(
          authToken,
          plaidId,
        );
        const { token } = await onboardingapi.get(authToken!, '/banking/verify');
        localStorage.setItem(`linkToken.${registration!.merchant_id}`, token);
        setLinkToken(token);
        await updateAccountNumbers();
        setShowAccountRow('inherit');
        setUpdatingAccounts(false);
        // required after server registration change to ensure Registration.Context stays in sync
        dispatch(
          updateRegistration({ ...registration!, ...registrationChanges }),
        );
        return true;
      } catch (err) {
        setUpdatingAccounts(false);
        setShowAccountRow('inherit');
        return false;
      }
    };

    const removeAccountFields = async () => {
      const payload: Partial<Registration> = {
        bank_account_number: null,
        bank_account_owner_name: null,
        bank_routing_number: null,
        bank_institution_name: null,
      };

      try {
        setUpdatingAccounts(true);
        const r = await coreapi.put(authToken!, '/team/registration', payload);
        setUpdatingAccounts(false);
        dispatch(updateRegistration(r));
        return true;
      } catch (error) {
        setUpdatingAccounts(false);
        toaster(toast.error(error, 'There was a problem saving your changes.'));
        return false;
      }
    };

    return (
      <Group
        direction={'row'}
        className={'plaid-account-row'}
        style={{
          marginBottom: '1rem',
          border: `1px solid ${theme.colors.core.gray[100].hex}`,
          padding: '1rem 1.2rem',
          display: key === 0 ? showPrimaryRow : showSecondaryRow,
        }}
        key={account.plaid_token_id}
      >
        <AccountUnlinkModal
          isOpen={
            account.account_type === 'primary'
              ? showPrimaryModal
              : showSecondaryModal
          }
          showModal={
            account.account_type === 'primary'
              ? setShowPrimaryModal
              : setShowSecondaryModal
          }
          maskedAccount={account.mask!}
          removeAccount={
            account.plaid_token_id ? removeAccountPlaid : removeAccountFields
          }
        />
        <div>
          <img
            alt="institution logo"
            src={account.logo ? "data:image/png;base64, " + account.logo : BankGenericIcon}
            style={{
              width: 54,
              height: 54,
              borderRadius: '6px',
              border: '1px solid #D9D9D9',
            }}
          />
        </div>
        <Group
          direction={'row'}
          className={'plaid-account-display'}
          style={{ margin: '0 2rem', padding: '5px 0' }}
        >
          <strong style={{ fontWeight: 'bolder' }}>
            {account.name} **{account.mask}
          </strong>
        </Group>
        <Group
          direction={'row'}
          className={'plaid-account-actions'}
          style={{ flexGrow: 1, justifyContent: 'end' }}
        >
          {key === accounts.length - 1 && (
            <Button
              variant="plain"
              motif="destructive"
              id={`delete-linked-plaid-account-${key}`}
              data-testid={`delete-linked-plaid-account-${key}-btn`}
              size="normal"
              icon="fas fa-times"
              style={{
                display: 'inline-block',
                color: '#E30029',
              }}
              disabled={updatingAccounts}
              onClick={() => {
                account.account_type === 'primary'
                  ? setShowPrimaryModal(true)
                  : setShowSecondaryModal(true);
              }}
            ></Button>
          )}
        </Group>
      </Group>
    );
  });

  return <Group direction={'column'}>{accountList}</Group>;
};
